<template>
  <div class="amc-mock-container">
    <!-- 顶部导航栏 -->
    <div class="amc-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-right" v-if="currentView === 'practice'">
        <el-radio-group v-model="language" size="mini" style="margin-right:10px;">
          <el-radio-button label="cn">中文</el-radio-button>
          <el-radio-button label="en">English</el-radio-button>
          <el-radio-button label="both">双语</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- ========== 选择模式首页 ========== -->
    <div v-if="currentView === 'home'" class="home-view">
      <!-- 模式切换 Tabs -->
      <div class="mode-tabs">
        <div class="mode-tab" :class="{ active: activeMode === 'preset' }" @click="activeMode = 'preset'">
          <i class="el-icon-document-copy"></i> 模拟卷
        </div>
        <div class="mode-tab" :class="{ active: activeMode === 'custom' }" @click="activeMode = 'custom'">
          <i class="el-icon-magic-stick"></i> 自行组卷
        </div>
      </div>

      <!-- ===== 模拟卷模式 ===== -->
      <div v-if="activeMode === 'preset'" class="mode-content">
        <div v-if="loadingList" class="loading-container"><i class="el-icon-loading"></i> 加载中...</div>
        <div v-else-if="presetPapers.length === 0" class="empty-hint">
          <i class="el-icon-folder-opened" style="font-size:48px;color:#ccc;"></i>
          <p>暂无预设模拟卷</p>
        </div>
        <div v-else class="paper-grid">
          <div v-for="p in presetPapers" :key="p.id" class="paper-card" @click="enterPaper(p)">
            <div class="paper-card-icon"><i class="el-icon-notebook-2"></i></div>
            <div class="paper-card-info">
              <div class="paper-card-title">{{ p.title }}</div>
              <div class="paper-card-meta">{{ p.totalQuestions || '?' }} 题</div>
            </div>
            <el-button type="primary" size="mini" class="paper-card-btn">开始</el-button>
          </div>
        </div>
      </div>

      <!-- ===== 自行组卷模式 ===== -->
      <div v-if="activeMode === 'custom'" class="mode-content">
        <!-- 组卷配置表单 -->
        <div class="custom-form-card">
          <h3 class="form-title"><i class="el-icon-setting"></i> 组卷设置</h3>
          <el-form label-width="100px" size="small">
            <el-form-item label="总题数">
              <el-input-number v-model="customConfig.totalQuestions" :min="5" :max="50" :step="5"></el-input-number>
            </el-form-item>
            <el-form-item label="难度分布">
              <div class="diff-sliders">
                <div class="diff-row">
                  <span class="diff-label beginner-label">入门</span>
                  <el-slider v-model="customConfig.beginnerCount" :max="50" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ customConfig.beginnerCount }}</span>
                </div>
                <div class="diff-row">
                  <span class="diff-label easy-label">简单</span>
                  <el-slider v-model="customConfig.easyCount" :max="50" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ customConfig.easyCount }}</span>
                </div>
                <div class="diff-row">
                  <span class="diff-label medium-label">一般</span>
                  <el-slider v-model="customConfig.mediumCount" :max="50" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ customConfig.mediumCount }}</span>
                </div>
                <div class="diff-row">
                  <span class="diff-label hard-label">困难</span>
                  <el-slider v-model="customConfig.hardCount" :max="50" :step="1" style="flex:1;"></el-slider>
                  <span class="diff-val">{{ customConfig.hardCount }}</span>
                </div>
              </div>
              <div class="diff-tip">默认按 25 题规则预设为 4 / 6 / 10 / 5，你可以自行调整；四档题数之和需等于总题数。</div>
              <div class="diff-actions">
                <el-button size="mini" @click="resetDifficultyConfig">恢复默认分布</el-button>
              </div>
            </el-form-item>
            <el-form-item label="包含错题">
              <el-switch v-model="customConfig.includeWrong"></el-switch>
              <span v-if="customConfig.includeWrong" style="margin-left:16px;">
                错题占比
                <el-input-number v-model="customConfig.wrongRatio" :min="10" :max="80" :step="10" size="mini" style="width:120px;margin-left:4px;"></el-input-number>%
              </span>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-magic-stick" :loading="generating" @click="generatePaper">立即组卷</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 用户历史组卷记录 -->
        <div class="history-section">
          <h3 class="section-title"><i class="el-icon-time"></i> 我的组卷记录</h3>
          <div v-if="loadingList" class="loading-container"><i class="el-icon-loading"></i> 加载中...</div>
          <div v-else-if="userPapers.length === 0" class="empty-hint">
            <p>暂无组卷记录，试试立即组一套吧！</p>
          </div>
          <div v-else class="paper-grid">
            <div v-for="p in userPapers" :key="p.id" class="paper-card" :class="{ completed: p.status === 'completed' }">
              <div class="paper-card-icon">
                <i :class="p.status === 'completed' ? 'el-icon-check' : 'el-icon-document'"></i>
              </div>
              <div class="paper-card-info">
                <div class="paper-card-title">{{ p.title }}</div>
                <div class="paper-card-meta">
                  {{ p.totalQuestions || '?' }} 题
                  <span v-if="p.status === 'completed'" class="score-badge">
                    得分 {{ p.score != null ? p.score : '-' }}
                  </span>
                  <span v-if="p.includeWrong" class="wrong-badge">含错题</span>
                </div>
                <div class="paper-card-time">{{ formatTime(p.createdAt) }}</div>
              </div>
              <div class="paper-card-actions">
                <el-button :type="p.status === 'completed' ? 'success' : 'primary'" size="mini" @click="enterPaper(p)">
                  {{ p.status === 'completed' ? '查看' : '做题' }}
                </el-button>
                <!-- <el-button type="danger" size="mini" icon="el-icon-delete" circle @click.stop="deletePaper(p)"></el-button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== 做题视图 ========== -->
    <div v-if="currentView === 'practice'" class="practice-view">
      <!-- 加载/错误/空 -->
      <div v-if="loading" class="loading-container"><i class="el-icon-loading"></i> 正在加载试卷...</div>
      <div v-else-if="error" class="error-container">
        <i class="el-icon-warning"></i> {{ error }}
        <div style="margin-top:8px"><el-button type="primary" size="small" @click="loadPaperQuestions">重试</el-button></div>
      </div>
      <div v-else-if="questions.length === 0" class="empty-container">
        <i class="el-icon-document"></i> 暂无题目数据
      </div>

      <div v-else class="paper-body">
        <div class="question-nav-panel" :class="{ collapsed: navCollapsed }">
          <div class="nav-header" @click="navCollapsed = !navCollapsed">
            <span class="nav-title">
              <i :class="navCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
              questionOverview
            </span>
            <div class="nav-overview-stats">
              <div class="nav-overview-stat-item">
                <div class="nav-overview-stat-number">{{ questions.length }}</div>
                <div class="nav-overview-stat-label">题目总数</div>
              </div>
              <div class="nav-overview-stat-item">
                <div class="nav-overview-stat-number">{{ answeredCount }}</div>
                <div class="nav-overview-stat-label">已答题数</div>
              </div>
              <div class="nav-overview-stat-item">
                <div class="nav-overview-stat-number">{{ correctCount }}</div>
                <div class="nav-overview-stat-label">答对题数</div>
              </div>
              <div class="nav-overview-stat-item">
                <div class="nav-overview-stat-number">{{ accuracy }}%</div>
                <div class="nav-overview-stat-label">正确率</div>
              </div>
              <div class="nav-overview-stat-item">
                <div class="nav-overview-stat-number">{{ isReviewMode ? '已完成' : formattedElapsed }}</div>
                <div class="nav-overview-stat-label">{{ isReviewMode ? '状态' : '用时' }}</div>
              </div>
            </div>
            <span class="nav-stats">{{ currentIndex + 1 }} / {{ questions.length }}</span>
          </div>
          <div class="nav-content" v-show="!navCollapsed">
            <div class="nav-grid">
              <div
                v-for="(q, idx) in questions"
                :key="`nav-${q.id}-${idx}`"
                class="nav-item"
                :class="{
                  active: idx === currentIndex,
                  answered: answers[idx] && String(answers[idx]).trim() !== '',
                  correct: results[idx] && results[idx].isCorrect,
                  wrong: results[idx] && !results[idx].isCorrect
                }"
                @click="jumpToQuestion(idx)"
              >
                <span class="nav-item-number">{{ idx + 1 }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="questions-container" v-if="currentQuestion">
          <div
            class="question-item"
            :class="{
              answered: answers[currentIndex],
              'submitted-wrong': (showAnswers || isReviewMode) && results[currentIndex] && !results[currentIndex].isCorrect,
              'submitted-correct': (showAnswers || isReviewMode) && results[currentIndex] && results[currentIndex].isCorrect
            }"
          >
            <div class="question-header single-question-header">
              <span class="question-number">第 {{ currentIndex + 1 }} 题 / Question {{ currentIndex + 1 }}</span>
              <div class="question-meta">
                <span class="question-year" v-if="currentQuestion.year || currentQuestion.source">{{ currentQuestion.year || currentQuestion.source }}</span>
              </div>
            </div>

            <div class="question-submeta" v-if="currentQuestion.number || currentQuestion.source">
              <span v-if="currentQuestion.number">编号：{{ currentQuestion.number }}</span>
              <span v-if="currentQuestion.source">来源：{{ currentQuestion.source }}</span>
            </div>

            <div class="question-content" v-html="formatContent(getContent(currentQuestion))"></div>

            <div class="question-images" v-if="getQuestionImages(currentQuestion).length">
              <img
                v-for="(imgUrl, imgIdx) in getQuestionImages(currentQuestion)"
                :key="`img-${currentQuestion.id}-${imgIdx}`"
                :src="imgUrl"
                class="question-img"
                @click="previewImage(imgUrl)"
              />
            </div>

            <div class="options" v-if="parseOptions(currentQuestion.optionsStr || getOptionsSource(currentQuestion)).length > 0 || hasOptionImages(currentQuestion)">
              <el-radio-group v-model="answers[currentIndex]" :disabled="isReviewMode" @change="onAnswerChange(currentIndex)">
                <el-radio v-for="(opt, optIdx) in parseOptions(currentQuestion.optionsStr || getOptionsSource(currentQuestion))" :key="opt.key" :label="opt.key" class="option-item">
                  <template v-if="getOptionImage(currentQuestion, optIdx)">
                    <div class="option-image-wrap">
                      <span class="option-key-label">{{ opt.key }}.</span>
                      <img
                        :src="getMinioUrl(getOptionImage(currentQuestion, optIdx))"
                        class="option-img"
                        @click="previewImage(getMinioUrl(getOptionImage(currentQuestion, optIdx)))"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <span class="option-rich" v-html="formatContent(`${opt.key}. ${opt.value}`)"></span>
                  </template>
                </el-radio>
              </el-radio-group>
            </div>
            <div class="answer-input" v-else>
              <el-input v-model="answers[currentIndex]" :disabled="isReviewMode" placeholder="请输入答案" @input="onAnswerChange(currentIndex)" style="width:220px" />
            </div>

            <div v-if="showAnswers || isReviewMode" class="correct-row">
              你的答案：
              <span class="user-answer">{{ getDisplayUserAnswer(currentIndex) || '-' }}</span>
            </div>

            <div v-if="showAnswers || isReviewMode" class="correct-row">
              正确答案：<span class="correct">{{ currentQuestion.answer }}</span>
            </div>

            <div v-if="(showAnswers || isReviewMode) && getDisplayExplanation(currentQuestion)" class="analysis-row">
              <div class="analysis-label">解析：</div>
              <div class="analysis-content" v-html="formatContent(getDisplayExplanation(currentQuestion))"></div>
            </div>

            <div class="question-nav-actions">
              <el-button size="small" @click="prevQuestion" :disabled="currentIndex === 0">上一题</el-button>
              <el-button size="small" type="primary" @click="nextQuestion" :disabled="currentIndex >= questions.length - 1">下一题</el-button>
              <span class="question-progress">第 {{ currentIndex + 1 }} / {{ questions.length }} 题</span>
            </div>
          </div>
        </div>

        <div class="paper-actions">
          <el-button @click="backToHome">返回列表</el-button>
          <el-button v-if="!isReviewMode" type="primary" size="large" :disabled="answeredCount===0" @click="submitPaper" :loading="submitting">提交答案</el-button>
          <el-button type="info" size="large" :disabled="!isReviewMode && answeredCount===0" @click="showAnswers = !showAnswers">{{ showAnswers ? '隐藏答案' : '显示答案' }}</el-button>
          <el-button type="warning" size="large" icon="el-icon-document" :disabled="questions.length === 0" @click="openPaperPreview">整页预览</el-button>
          <el-button v-if="!isReviewMode" size="large" @click="resetPaper">重置</el-button>
        </div>
      </div>
    </div>

    <!-- 结果弹窗 -->
    <el-dialog title="考试结果" :visible.sync="showResult" width="680px">
      <div class="result-grid">
        <div class="rg-item"><span class="k">总题数</span><span class="v">{{ questions.length }}</span></div>
        <div class="rg-item"><span class="k">已作答</span><span class="v">{{ answeredCount }}</span></div>
        <div class="rg-item"><span class="k">答对题</span><span class="v success">{{ correctCount }}</span></div>
        <div class="rg-item"><span class="k">答错题</span><span class="v danger">{{ wrongCount }}</span></div>
        <div class="rg-item score"><span class="k">得分</span><span class="v">{{ score }} 分</span></div>
      </div>
      <div class="result-toolbar" v-if="Array.isArray(results) && results.length">
        <el-radio-group v-model="resultViewMode" size="mini">
          <el-radio-button label="wrong">仅看错题 ({{ wrongCount }})</el-radio-button>
          <el-radio-button label="all">全部 ({{ results.length }})</el-radio-button>
        </el-radio-group>
      </div>
      <div v-if="Array.isArray(results) && results.length" class="wrong-list">
        <div class="item" v-for="w in displayedResultList" :key="w.id">
          <div class="title">第 {{ w.index + 1 }} 题</div>
          <div class="row"><span class="k">你的答案</span><span class="v danger">{{ w.user }}</span></div>
          <div class="row"><span class="k">正确答案</span><span class="v success">{{ w.correct }}</span></div>
          <div class="row" v-if="w.knowledgeText">
            <span class="k">知识点</span>
            <span class="v knowledge-text">{{ w.knowledgeText }}</span>
          </div>
          <div class="row" v-if="w.content">
            <span class="k">题目</span>
            <div class="v">
              <div class="collapsible" :class="{ expanded: isExpanded(w, 'content') }">
                <div v-html="formatContent(w.content)"></div>
              </div>
              <el-link type="primary" :underline="false" class="toggle-link" @click="toggleExpand(w, 'content')">
                {{ isExpanded(w, 'content') ? '收起' : '展开' }}
              </el-link>
            </div>
          </div>
          <div class="row" v-if="w.explanation">
            <span class="k">解析</span>
            <div class="v">
              <div class="collapsible" :class="{ expanded: isExpanded(w, 'explanation') }">
                <div v-html="formatContent(w.explanation)"></div>
              </div>
              <el-link type="primary" :underline="false" class="toggle-link" @click="toggleExpand(w, 'explanation')">
                {{ isExpanded(w, 'explanation') ? '收起' : '展开' }}
              </el-link>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showResult = false">关闭</el-button>
        <el-button type="primary" @click="backToHome">返回列表</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="`${currentPaperTitle} 试卷预览（共 ${questions.length} 题）`"
      :visible.sync="paperPreviewVisible"
      fullscreen
      :close-on-click-modal="false"
      custom-class="paper-preview-dialog"
    >
      <div slot="title" style="display:flex; align-items:center; justify-content:space-between; padding-right:40px;">
        <span style="font-size:16px; font-weight:600;">{{ currentPaperTitle }} 试卷预览（共 {{ questions.length }} 题）</span>
        <el-button type="primary" size="small" icon="el-icon-printer" @click="printPaper">打印 / 导出PDF</el-button>
      </div>
      <div style="background:#e8e8e8; min-height:100%; padding:24px 0;">
        <div id="paper-print-area" class="a4-paper">
          <div class="paper-header-print">
            <div class="paper-title-print">{{ currentPaperTitle }}</div>
            <div class="paper-meta-print">共 {{ questions.length }} 题</div>
          </div>
          <div class="paper-divider-print"></div>

          <div v-for="(q, idx) in questions" :key="`print-${q.id || idx}`" class="paper-question-print">
            <div class="pq-stem-print">
              <span class="pq-no-print">{{ idx + 1 }}.</span>
              <span v-html="formatContent(getContent(q))"></span>
            </div>
            <div class="pq-img-wrap" v-if="getQuestionImages(q).length">
              <img v-for="(imgUrl, imgIdx) in getQuestionImages(q)" :key="`pimg-${idx}-${imgIdx}`" :src="imgUrl" class="pq-img" />
            </div>
            <div class="pq-options-print" v-if="q.optionsStr && parseOptions(q.optionsStr).length > 0">
              <div
                v-for="opt in parseOptions(q.optionsStr)"
                :key="`print-opt-${idx}-${opt.key}`"
                class="pq-option-print"
              >
                <span v-html="formatContent(`${opt.key}. ${opt.value}`)"></span>
              </div>
            </div>
          </div>

          <div v-if="questions.length === 0" class="paper-empty-print">暂无题目可预览</div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listPresetPapers, listUserPapers, getPaperDetail, generateCustomPaper, submitPaperResult, deleteUserPaper } from '@/api/mockTest'
import { listPublicFeatures } from '@/api/feature'
import { listGeneralKnowledgeTree } from '@/api/knowledge'
import { getMinioUrl } from '@/utils/minio'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'AMCMockTest',
  data() {
    return {
      // 视图状态: home / practice
      currentView: 'home',
      activeMode: 'preset',

      // 列表数据
      loadingList: false,
      presetPapers: [],
      userPapers: [],

      // 组卷配置
      customConfig: {
        totalQuestions: 25,
        beginnerCount: 4,
        easyCount: 6,
        mediumCount: 10,
        hardCount: 5,
        includeWrong: false,
        wrongRatio: 30
      },
      generating: false,

      // 做题状态
      loading: false,
      error: null,
      navCollapsed: false,
      currentIndex: 0,
      repoId: null,
      currentPaperId: null,
      currentPaper: null,
      questions: [],
      answers: [],
      language: 'both',
      elapsedSeconds: 0,
      timer: null,
      showAnswers: false,
      showResult: false,
      results: [],
      wrongList: [],
      resultViewMode: 'wrong',
      knowledgeNameMap: {},
      expandState: {},
      submitting: false,
      paperPreviewVisible: false,
      featureEnabled: true,
      featureChecked: false
    }
  },
  computed: {
    pageTitle() {
      if (this.currentView === 'practice') return '模拟测试'
      return '模拟考试'
    },
    formattedElapsed() {
      const total = Number(this.elapsedSeconds || 0)
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const seconds = total % 60
      const mm = String(minutes).padStart(2, '0')
      const ss = String(seconds).padStart(2, '0')
      if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${mm}:${ss}`
      }
      return `${mm}:${ss}`
    },
    isReviewMode() {
      return this.currentPaper && this.currentPaper.status === 'completed'
    },
    answeredCount() {
      if (this.isReviewMode && this.currentPaper && this.currentPaper.correctCount != null) {
        return this.currentPaper.totalQuestions || this.answers.length || this.questions.length
      }
      return this.answers.filter(a => a && String(a).trim() !== '').length
    },
    correctCount() {
      if (this.isReviewMode && this.currentPaper && this.currentPaper.correctCount != null) {
        return Number(this.currentPaper.correctCount || 0)
      }
      return this.results.filter(r => r.isCorrect).length
    },
    wrongCount() {
      if (this.isReviewMode && this.currentPaper && this.currentPaper.totalQuestions != null) {
        return Math.max(Number(this.currentPaper.totalQuestions || 0) - Number(this.correctCount || 0), 0)
      }
      return this.results.filter(r => !r.isCorrect).length
    },
    accuracy() {
      if (this.isReviewMode && this.currentPaper && this.currentPaper.totalQuestions) {
        return Math.round((this.correctCount / (this.currentPaper.totalQuestions || 1)) * 100)
      }
      return this.questions.length ? Math.round((this.correctCount / (this.questions.length || 1)) * 100) : 0
    },
    score() {
      if (this.isReviewMode && this.currentPaper && this.currentPaper.score != null) {
        return Number(this.currentPaper.score || 0)
      }
      return Math.round((this.correctCount / (this.questions.length || 1)) * 100)
    },
    displayedResultList() {
      const list = this.resultViewMode === 'all' ? this.results : this.wrongList
      return Array.isArray(list) ? list : []
    },
    customDifficultyConfig() {
      return {
        beginner: Number(this.customConfig.beginnerCount || 0),
        easy: Number(this.customConfig.easyCount || 0),
        medium: Number(this.customConfig.mediumCount || 0),
        hard: Number(this.customConfig.hardCount || 0)
      }
    },
    currentPaperTitle() {
      if (this.currentPaper && this.currentPaper.title) return this.currentPaper.title
      return 'AMC 自行组卷'
    },
    currentQuestion() {
      return this.questions[this.currentIndex] || null
    }
  },
  created() {
    this.repoId = this.$route.query.repoId
    if (!this.repoId) {
      this.$message.error('缺少题库ID参数')
      this.$router.go(-1)
      return
    }
    this.initializePage()
  },
  mounted() {
    try {
      this.$store && this.$store.dispatch && this.$store.dispatch('app/closeSideBar', { withoutAnimation: true })
    } catch (e) { /* no-op */ }
  },
  beforeDestroy() {
    this.stopTimer()
  },
  methods: {
    async initializePage() {
      const enabled = await this.checkFeatureAvailability()
      if (!enabled) {
        return
      }
      this.loadKnowledgeNameMap()
      this.loadPaperLists()
    },

    isKangarooCourse() {
      const courseType = String(this.$route.query.courseType || '').toUpperCase()
      const subjectCode = String(this.$route.query.subjectCode || '').toUpperCase()
      const repoTitle = String(this.$route.query.repoTitle || '').toUpperCase()
      return courseType.includes('KANGAROO') || subjectCode.includes('KANGAROO') || subjectCode.includes('袋鼠'.toUpperCase()) || repoTitle.includes('KANGAROO')
    },

    getFeatureCode() {
      if (this.isKangarooCourse()) {
        return 'KANGAROO_MOCK_TEST'
      }
      return null
    },

    async checkFeatureAvailability() {
      if (this.featureChecked) {
        return this.featureEnabled
      }
      const featureCode = this.getFeatureCode()
      if (!featureCode) {
        this.featureEnabled = true
        this.featureChecked = true
        return true
      }
      try {
        const res = await listPublicFeatures()
        const list = res?.data?.data || res?.data || []
        const feature = Array.isArray(list) ? list.find(item => item && item.featureCode === featureCode) : null
        const enabled = !feature || Number(feature.status) === 1
        this.featureEnabled = enabled
        this.featureChecked = true
        if (!enabled) {
          this.$message.warning('功能开发中')
          this.$router.go(-1)
        }
        return enabled
      } catch (e) {
        console.warn('功能开关检查失败:', e)
        this.featureEnabled = true
        this.featureChecked = true
        return true
      }
    },

    // ==================== 列表加载 ====================
    async loadPaperLists() {
      if (!(await this.checkFeatureAvailability())) {
        return
      }
      this.loadingList = true
      try {
        const [presetRes, userRes] = await Promise.allSettled([
          listPresetPapers(this.repoId),
          listUserPapers(this.repoId)
        ])
        this.presetPapers = this.extractList(presetRes)
        this.userPapers = this.extractList(userRes)
      } catch (e) {
        console.warn('加载试卷列表失败:', e)
      } finally {
        this.loadingList = false
      }
    },

    extractList(settled) {
      if (settled.status !== 'fulfilled') return []
      const res = settled.value
      const data = res?.data?.data || res?.data || []
      return Array.isArray(data) ? data : []
    },

    // ==================== 进入试卷 ====================
    async enterPaper(paper) {
      if (!(await this.checkFeatureAvailability())) {
        return
      }
      this.currentPaper = paper
      this.currentPaperId = paper.id
      this.currentView = 'practice'
      await this.loadPaperQuestions()
    },

    async loadPaperQuestions() {
      this.loading = true
      this.error = null
      this.currentIndex = 0
      this.navCollapsed = false
      this.showAnswers = !!(this.currentPaper && this.currentPaper.status === 'completed')
      this.showResult = false
      this.results = []
      this.wrongList = []

      try {
        const res = await getPaperDetail(this.currentPaperId)
        const data = res?.data?.data || res?.data || {}
        this.currentPaper = data.paper || this.currentPaper
        const rawQuestions = data.questions || []
        if (!rawQuestions.length) {
          this.error = '试卷中暂无题目'
          this.questions = []
          return
        }
        this.questions = rawQuestions.map(q => this.normalizeQuestion(q))
        this.answers = new Array(this.questions.length).fill('')

        if (this.isReviewMode) {
          const detailMap = this.parseAnswerDetailMap(this.currentPaper && this.currentPaper.answerDetail)
          this.stopTimer()
          this.elapsedSeconds = 0
          this.results = this.questions.map((q, i) => ({
            id: q.id,
            index: i,
            user: detailMap[q.id] ? (detailMap[q.id].userAnswer || '-') : '-',
            correct: this.extractOptionKey(q.answer),
            isCorrect: detailMap[q.id] ? !!detailMap[q.id].isCorrect : false,
            knowledge: q.knowledge || '',
            knowledgeText: this.resolveKnowledgeNames(q.knowledge),
            content: this.getContent(q) || '',
            explanation: this.getExplanation(q) || ''
          }))
          this.answers = this.questions.map(q => detailMap[q.id] ? (detailMap[q.id].userAnswer || '') : '')
          this.wrongList = this.results.filter(r => !r.isCorrect)
        } else {
          this.resetTimer()
          this.startTimer()
        }
      } catch (e) {
        console.warn('加载试卷题目失败:', e)
        this.error = '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    normalizeQuestion(q) {
      const contentCn = q.contentCn || q.content_cn || ''
      const contentEn = q.contentEn || q.content_en || q.content || q.question || ''
      const analysisCn = q.analysisCn || q.analysis_cn || ''
      const analysisEn = q.analysisEn || q.analysis_en || q.analysis || q.judge || ''
      return {
        id: q.id,
        contentCn, contentEn, analysisCn, analysisEn,
        content: this.combineCnEn(contentCn, contentEn),
        analysis: this.combineCnEn(analysisCn, analysisEn),
        optionsStr: q.optionsCn || q.optionsEn || q.optionsStr || q.options || q.choices || '',
        answer: q.answer || q.correctAnswer || '',
        knowledge: q.knowledge || q.knowledgePoint || '',
        knowledgeText: this.resolveKnowledgeNames(q.knowledge || q.knowledgePoint || ''),
        source: q.source || '',
        year: q.year || '',
        number: q.number || q.title || '',
        difficulty: q.difficulty || '',
        imagePaths: q.imagePaths || q.image_paths || '',
        image: q.image || ''
      }
    },

    async loadKnowledgeNameMap() {
      try {
        const res = await listGeneralKnowledgeTree(this.repoId)
        const tree = res?.data?.data || res?.data || []
        const map = {}
        const walk = (nodes) => {
          if (!Array.isArray(nodes)) return
          nodes.forEach(node => {
            const id = node?.id != null ? String(node.id) : ''
            const name = node?.content || node?.name || node?.label || ''
            if (id && name) map[id] = name
            walk(node?.children || [])
          })
        }
        walk(tree)
        this.knowledgeNameMap = map
      } catch (e) {
        console.warn('加载知识点树失败:', e)
      }
    },

    resolveKnowledgeNames(knowledge) {
      if (!knowledge) return ''
      return String(knowledge)
        .split(',')
        .map(item => String(item).trim())
        .filter(Boolean)
        .map(id => this.knowledgeNameMap[id] || id)
        .join(' / ')
    },

    parseAnswerDetailMap(answerDetail) {
      if (!answerDetail) return {}
      try {
        const list = typeof answerDetail === 'string' ? JSON.parse(answerDetail) : answerDetail
        if (!Array.isArray(list)) return {}
        return list.reduce((acc, item) => {
          if (item && item.questionId != null) {
            acc[item.questionId] = item
          }
          return acc
        }, {})
      } catch (e) {
        return {}
      }
    },

    getAnswerDetailItem(questionId) {
      const map = this.parseAnswerDetailMap(this.currentPaper && this.currentPaper.answerDetail)
      return map[questionId] || null
    },

    getDisplayUserAnswer(index) {
      const question = this.questions[index]
      if (!question) return ''
      const detail = this.getAnswerDetailItem(question.id)
      if (detail && detail.userAnswer != null && String(detail.userAnswer).trim() !== '') {
        return detail.userAnswer
      }
      return this.answers[index] || ''
    },

    getDisplayExplanation(question) {
      return this.getExplanation(question)
    },

    startTimer() {
      this.stopTimer()
      this.timer = setInterval(() => {
        this.elapsedSeconds += 1
      }, 1000)
    },

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    resetTimer() {
      this.stopTimer()
      this.elapsedSeconds = 0
    },

    resetDifficultyConfig() {
      this.customConfig.beginnerCount = 4
      this.customConfig.easyCount = 6
      this.customConfig.mediumCount = 10
      this.customConfig.hardCount = 5
      this.customConfig.totalQuestions = 25
    },

    // ==================== 随机组卷 ====================
    async generatePaper() {
      if (!(await this.checkFeatureAvailability())) {
        return
      }
      const total = this.customConfig.totalQuestions
      const difficultySum = Object.values(this.customDifficultyConfig).reduce((sum, count) => sum + Number(count || 0), 0)
      if (difficultySum !== Number(total || 0)) {
        this.$message.warning(`难度分布总和需等于总题数，当前为 ${difficultySum}/${total}`)
        return
      }
      const difficultyConfig = JSON.stringify(this.customDifficultyConfig)

      this.generating = true
      try {
        const res = await generateCustomPaper({
          repoId: Number(this.repoId),
          totalQuestions: total,
          difficultyConfig,
          includeWrong: this.customConfig.includeWrong,
          wrongRatio: this.customConfig.includeWrong ? this.customConfig.wrongRatio : 0,
          courseType: this.$route.query.courseType || null
        })
        const paper = res?.data?.data || res?.data || null
        if (paper && paper.id) {
          this.$message.success('组卷成功！')
          // 刷新列表并进入做题
          await this.loadPaperLists()
          this.enterPaper(paper)
        } else {
          this.$message.error(res?.data?.msg || '组卷失败')
        }
      } catch (e) {
        console.warn('组卷失败:', e)
        this.$message.error('组卷失败，请稍后重试')
      } finally {
        this.generating = false
      }
    },

    // ==================== 删除试卷 ====================
    async deletePaper(paper) {
      try {
        await this.$confirm('确认删除此试卷？', '提示', { type: 'warning' })
        await deleteUserPaper(paper.id)
        this.$message.success('已删除')
        this.userPapers = this.userPapers.filter(p => p.id !== paper.id)
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败')
      }
    },

    // ==================== 做题逻辑 ====================
    onAnswerChange() {},

    prevQuestion() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },

    nextQuestion() {
      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex += 1
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },

    jumpToQuestion(index) {
      if (index >= 0 && index < this.questions.length && index !== this.currentIndex) {
        this.currentIndex = index
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },

    // 获取选项来源字段
    getOptionsSource(q) {
      if (!q) return ''
      return q.optionsCn || q.optionsEn || q.options || q.optionsStr || ''
    },

    parseOptions(optionsStr) {
      if (!optionsStr) return []
      const s = this.normalizeOptions(String(optionsStr))
      const opts = []
      const re = /\(([A-Z])\)\s*([^\n;]*)/g
      let m
      while ((m = re.exec(s)) !== null) {
        opts.push({ key: m[1], value: m[2].trim() })
      }
      return opts
    },

    normalizeOptions(raw) {
      if (!raw) return ''
      let s = String(raw).trim()
      s = s
        .replace(/(^|[;\s])A\s*\.?\s*/g, '$1(A) ')
        .replace(/(^|[;\s])B\s*\.?\s*/g, '$1(B) ')
        .replace(/(^|[;\s])C\s*\.?\s*/g, '$1(C) ')
        .replace(/(^|[;\s])D\s*\.?\s*/g, '$1(D) ')
        .replace(/(^|[;\s])E\s*\.?\s*/g, '$1(E) ')
      return s
    },

    combineCnEn(cn, en) {
      const hasCn = cn && String(cn).trim() && String(cn).trim() !== '暂无'
      const hasEn = en && String(en).trim()
      if (hasCn && hasEn) return `<div class="lang-cn">${cn}</div><div class="lang-en" style="margin-top:8px;color:#555;">${en}</div>`
      if (hasCn) return String(cn)
      if (hasEn) return String(en)
      return ''
    },

    getQuestionImages(q) {
      if (!q) return []
      const paths = q.imagePaths || q.image_paths || q.image || ''
      if (!paths) return []
      const convertPath = (path) => {
        if (!path) return ''
        const trimmed = String(path).trim()
        if (!trimmed) return ''
        if (trimmed.startsWith('http')) return trimmed
        if (trimmed.startsWith('/api/file/')) return trimmed
        try { return getMinioUrl(trimmed) } catch (_) {}
        return trimmed
      }
      try {
        const arr = typeof paths === 'string' ? JSON.parse(paths) : paths
        if (Array.isArray(arr)) {
          return arr.filter(p => p && String(p).trim()).map(p => convertPath(p)).filter(Boolean)
        }
      } catch (e) {
        // not JSON, treat as semicolon/comma separated paths
        if (typeof paths === 'string' && paths.trim()) {
          return paths.split(/[;,]/).map(p => convertPath(p.trim())).filter(Boolean)
        }
      }
      return []
    },

    // 判断题目是否有选项图片
    hasOptionImages(question) {
      if (!question) return false
      const raw = question.optionImages || question.option_images
      if (!raw) return false
      if (Array.isArray(raw)) return raw.some(item => String(item || '').trim())
      if (typeof raw === 'string') return !!raw.trim()
      return false
    },

    // 获取选项图片
    getOptionImage(question, optionIndex) {
      if (!question) return null
      const raw = question.optionImages || question.option_images
      if (!raw) return null
      let imagePaths = []
      if (Array.isArray(raw)) {
        imagePaths = raw.map(item => String(item || '').trim()).filter(Boolean)
      } else if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return null
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            imagePaths = parsed.map(item => String(item || '').trim()).filter(Boolean)
          }
        } catch (e) {
          imagePaths = trimmed.split(/[;,]/).map(item => item.trim()).filter(Boolean)
        }
      }
      return imagePaths[optionIndex] || null
    },

    previewImage(url) {
      this.$alert(`<img src="${url}" style="max-width:100%;max-height:80vh;display:block;margin:0 auto;" />`, '图片预览', {
        dangerouslyUseHTMLString: true,
        showCancelButton: false,
        confirmButtonText: '关闭',
        customClass: 'image-preview-dialog'
      }).catch(() => {})
    },

    getContent(q) {
      if (!q) return ''
      const cn = q.contentCn || ''
      const en = q.contentEn || ''
      if (this.language === 'cn') return cn || en || ''
      if (this.language === 'en') return en || cn || ''
      return this.combineCnEn(cn, en)
    },
    getExplanation(q) {
      if (!q) return ''
      const cn = q.analysisCn || ''
      const en = q.analysisEn || ''
      if (this.language === 'cn') return cn || en || ''
      if (this.language === 'en') return en || cn || ''
      return this.combineCnEn(cn, en)
    },

    renderMathFormula(text) {
      if (!text) return ''
      const safe = String(text).replace(/\\\\\(/g, '\\(').replace(/\\\\\)/g, '\\)').replace(/\\\\\[/g, '\\[').replace(/\\\\\]/g, '\\]')
      try {
        let html = safe.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: true, strict: false })
          } catch (e) {
            return match
          }
        })
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: true, strict: false })
          } catch (e) {
            return match
          }
        })
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: false, strict: false })
          } catch (e) {
            return match
          }
        })
        html = html.replace(/\$(?!\$)([^$\n]+?)\$(?!\$)/g, (match, formula) => {
          try {
            return katex.renderToString(formula, { throwOnError: false, displayMode: false, strict: false })
          } catch (e) {
            return match
          }
        })
        return html.replace(/\n/g, '<br>')
      } catch (e) {
        return safe.replace(/\n/g, '<br>')
      }
    },

    extractOptionKey(val) {
      if (!val) return ''
      const s = String(val).toUpperCase()
      const m = s.match(/[\(\s]?([A-E])[\)\s\.]?/)
      return m ? m[1] : s.trim()
    },

    getFullOptionContent(question, optionKey) {
      if (!optionKey || !question || !question.optionsStr) return optionKey || ''
      const opts = this.parseOptions(question.optionsStr)
      const found = opts.find(o => o.key === optionKey)
      return found ? `${found.key} ${found.value}` : optionKey
    },

    async submitPaper() {
      if (this.isReviewMode) {
        return
      }
      if (!this.answeredCount) {
        this.$message.warning('请先作答后再交卷')
        return
      }
      this.submitting = true
      let loadingInstance = null
      try {
        try { loadingInstance = this.$loading({ lock: true, text: '批改中...', spinner: 'el-icon-loading', background: 'rgba(0,0,0,0.5)' }) } catch (e) {}

        const results = []
        for (let i = 0; i < this.questions.length; i++) {
          const q = this.questions[i]
          const user = this.answers[i]
          if (!user || String(user).trim() === '') continue
          const userKey = this.extractOptionKey(user)
          const correctKey = this.extractOptionKey(q.answer)
          const isCorrect = userKey === correctKey
          results.push({
            id: q.id, index: i, user: userKey, correct: correctKey, isCorrect,
            knowledge: q.knowledge || '',
            knowledgeText: this.resolveKnowledgeNames(q.knowledge),
            content: this.getContent(q) || '',
            explanation: this.getExplanation(q) || ''
          })
        }

        this.results = results
        this.wrongList = results.filter(r => !r.isCorrect)
        this.stopTimer()
        this.showResult = true

        // 异步保存结果到后端
        if (this.currentPaperId) {
          const answerDetail = results.map(r => ({
            questionId: r.id,
            userAnswer: r.user,
            correctAnswer: r.correct,
            isCorrect: r.isCorrect,
            knowledge: this.questions[r.index]?.knowledge || '',
            aiInfo: null
          }))
          if (this.currentPaper) {
            this.currentPaper = {
              ...this.currentPaper,
              status: 'completed',
              score: this.score,
              correctCount: this.correctCount,
              answerDetail: JSON.stringify(answerDetail)
            }
          }
          submitPaperResult(this.currentPaperId, {
            score: this.score,
            correctCount: this.correctCount,
            answerDetail: JSON.stringify(answerDetail)
          }).catch(() => {})
        }
      } finally {
        if (loadingInstance && typeof loadingInstance.close === 'function') loadingInstance.close()
        this.submitting = false
      }
    },

    resetPaper() {
      if (this.isReviewMode) {
        return
      }
      this.answers = new Array(this.questions.length).fill('')
      this.results = []
      this.showAnswers = false
      this.resetTimer()
      this.startTimer()
    },

    openPaperPreview() {
      if (!this.questions.length) {
        this.$message.warning('暂无题目可预览')
        return
      }
      this.paperPreviewVisible = true
    },

    printPaper() {
      const el = document.getElementById('paper-print-area')
      if (!el) return
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>试卷预览</title><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css"><style>*{box-sizing:border-box;}body{margin:0;padding:20px;font-family:'Source Han Serif CN','Songti SC','SimSun',serif;font-size:14px;color:#111;background:#fff;}.a4-paper{width:794px;margin:0 auto;padding:60px 72px;}.paper-header-print{text-align:center;margin-bottom:12px;}.paper-title-print{font-size:22px;font-weight:700;}.paper-meta-print{font-size:13px;color:#666;margin-top:6px;}.paper-divider-print{border-top:2px solid #111;margin:16px 0 24px;}.paper-question-print{margin-bottom:24px;page-break-inside:avoid;}.pq-stem-print{display:flex;gap:6px;line-height:1.8;font-size:14px;}.pq-no-print{font-weight:700;min-width:24px;}.pq-options-print{margin-top:8px;margin-left:28px;display:grid;grid-template-columns:repeat(2,1fr);gap:4px 24px;}.pq-option-print{font-size:13px;line-height:1.7;}.katex-display{margin:0.35em 0;text-align:left;overflow-x:auto;overflow-y:hidden;}</style></head><body>${el.innerHTML}</body></html>`
      const w = window.open('', '_blank')
      if (!w) {
        this.$message.error('浏览器阻止了弹窗，请允许弹窗后重试')
        return
      }
      w.document.write(html)
      w.document.close()
      w.focus()
      setTimeout(() => { w.print() }, 600)
    },

    // ==================== 导航 ====================
    backToHome() {
      this.currentView = 'home'
      this.currentPaper = null
      this.showResult = false
      this.paperPreviewVisible = false
      this.stopTimer()
      this.loadPaperLists()
    },

    goBack() {
      if (this.currentView === 'practice') {
        this.backToHome()
      } else {
        this.$router.go(-1)
      }
    },

    // ==================== 工具 ====================
    formatContent(text) {
      return this.renderMathFormula(text)
    },
    formatTime(dt) {
      if (!dt) return ''
      return String(dt).replace('T', ' ').substring(0, 16)
    },
    toggleExpand(w, field) {
      const key = `${w.id}-${w.index}`
      if (!this.expandState[key]) this.$set(this.expandState, key, { content: false, explanation: false })
      this.$set(this.expandState[key], field, !this.expandState[key][field])
    },
    isExpanded(w, field) {
      const key = `${w.id}-${w.index}`
      return !!(this.expandState[key] && this.expandState[key][field])
    }
  }
}
</script>

<style scoped>
.amc-mock-container { min-height: 100vh; background: #f5f7fa; }
.amc-header { background: #fff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); position: sticky; top: 0; z-index: 10; }
.back-btn { font-size: 18px; color: #666; cursor: pointer; padding: 4px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 16px; font-weight: 600; }
.loading-container, .error-container, .empty-container { display: flex; align-items: center; justify-content: center; min-height: 200px; color: #666; gap: 8px; flex-direction: column; }

/* ===== 首页 ===== */
.home-view { padding: 20px; max-width: 960px; margin: 0 auto; }
.mode-tabs { display: flex; gap: 0; margin-bottom: 24px; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.mode-tab { flex: 1; padding: 16px; text-align: center; cursor: pointer; font-size: 15px; font-weight: 600; color: #666; transition: all 0.3s; border-bottom: 3px solid transparent; }
.mode-tab:hover { background: #f8f9fa; }
.mode-tab.active { color: #409eff; border-bottom-color: #409eff; background: #ecf5ff; }
.mode-tab i { margin-right: 6px; }

/* 试卷网格 */
.paper-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
.paper-card { display: flex; align-items: center; gap: 16px; background: #fff; border-radius: 12px; padding: 16px 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); cursor: pointer; transition: all 0.3s; border: 2px solid transparent; }
.paper-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-color: #409eff; }
.paper-card.completed { border-color: #67c23a; }
.paper-card-icon { width: 48px; height: 48px; border-radius: 12px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; flex-shrink: 0; }
.paper-card.completed .paper-card-icon { background: linear-gradient(135deg, #67c23a 0%, #4caf50 100%); }
.paper-card-info { flex: 1; }
.paper-card-title { font-size: 15px; font-weight: 600; color: #333; margin-bottom: 4px; }
.paper-card-meta { font-size: 13px; color: #999; display: flex; gap: 8px; align-items: center; }
.paper-card-time { font-size: 12px; color: #bbb; margin-top: 2px; }
.score-badge { background: #f0f9ff; color: #409eff; padding: 1px 6px; border-radius: 4px; font-size: 12px; }
.wrong-badge { background: #fff3e0; color: #e6a23c; padding: 1px 6px; border-radius: 4px; font-size: 12px; }
.paper-card-actions { display: flex; gap: 6px; align-items: center; }
.paper-card-btn { flex-shrink: 0; }

/* 空状态 */
.empty-hint { text-align: center; padding: 40px 20px; color: #999; }

/* 组卷配置 */
.custom-form-card { background: #fff; border-radius: 12px; padding: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 24px; }
.form-title { margin: 0 0 16px; font-size: 16px; color: #333; }
.form-title i { margin-right: 6px; color: #409eff; }
.diff-sliders { display: flex; flex-direction: column; gap: 8px; }
.diff-row { display: flex; align-items: center; gap: 12px; }
.diff-label { width: 48px; font-size: 13px; font-weight: 600; text-align: right; }
.diff-desc { flex: 1; color: #606266; font-size: 13px; }
.diff-tip { margin-top: 8px; color: #909399; font-size: 12px; line-height: 1.6; }
.diff-actions { margin-top: 8px; }
.beginner-label { color: #409eff; }
.easy-label { color: #67c23a; }
.medium-label { color: #e6a23c; }
.hard-label { color: #f56c6c; }
.diff-val { width: 40px; text-align: right; font-size: 13px; color: #666; }

/* 历史记录 */
.history-section { margin-top: 0; }
.section-title { font-size: 16px; color: #333; margin: 0 0 12px; }
.section-title i { margin-right: 6px; color: #909399; }

/* ===== 做题视图 ===== */
.practice-view { padding: 16px; max-width: 1200px; margin: 0 auto; }
.paper-body { padding: 0; }
.question-nav-panel { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 14px; margin-bottom: 18px; overflow: hidden; box-shadow: 0 6px 18px rgba(102, 126, 234, 0.18); color: #fff; }
.nav-header { display: flex; align-items: center; gap: 12px; padding: 14px 18px; cursor: pointer; }
.nav-title { display: flex; align-items: center; gap: 8px; font-size: 22px; font-weight: 600; min-width: 180px; }
.nav-overview-stats { display: flex; align-items: center; gap: 12px; flex: 1; justify-content: flex-end; margin-right: 8px; }
.nav-overview-stat-item { background: rgba(255, 255, 255, 0.15); border-radius: 10px; padding: 8px 12px; min-width: 90px; text-align: center; backdrop-filter: blur(8px); }
.nav-overview-stat-number { font-size: 20px; line-height: 1.1; font-weight: 700; }
.nav-overview-stat-label { margin-top: 2px; font-size: 12px; opacity: 0.9; }
.nav-stats { font-size: 14px; font-weight: 500; background: rgba(255, 255, 255, 0.2); padding: 4px 12px; border-radius: 12px; }
.nav-content { padding: 20px; max-height: 500px; overflow-y: auto; transition: max-height 0.3s ease; background: #fff; }
.nav-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(50px, 1fr)); gap: 10px; }
.nav-item { position: relative; width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; background: #f5f7fa; border: 2px solid #e4e7ed; border-radius: 8px; cursor: pointer; transition: all 0.2s ease; font-weight: 500; color: #606266; }
.nav-item:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-color: #667eea; }
.nav-item.active { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-color: #667eea; color: #fff; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); }
.nav-item.answered { background: #e8f4fd; border-color: #409eff; color: #409eff; }
.nav-item.correct { background: #f0f9ff; border-color: #52c41a; color: #52c41a; }
.nav-item.correct::after { content: '✓'; position: absolute; top: 2px; right: 2px; font-size: 10px; color: #52c41a; }
.nav-item.wrong { background: #fff2f0; border-color: #ff4d4f; color: #ff4d4f; }
.nav-item.wrong::after { content: '✗'; position: absolute; top: 2px; right: 2px; font-size: 10px; color: #ff4d4f; }
.nav-item-number { font-size: 14px; }
.questions-container { display: grid; gap: 16px; }
.question-item { background: #fff; border-radius: 14px; padding: 24px; box-shadow: 0 4px 14px rgba(0,0,0,0.06); border: 2px solid transparent; }
.question-item.answered { border-color: #d9ecff; }
.question-item.submitted-correct { border-color: #b7eb8f; background: #fcfff7; }
.question-item.submitted-wrong { border-color: #ffccc7; background: #fffafa; }
.single-question-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.question-number { font-size: 18px; font-weight: 700; color: #303133; }
.question-meta { display: flex; align-items: center; gap: 8px; }
.question-year { font-size: 12px; color: #909399; background: #f4f4f5; padding: 4px 8px; border-radius: 6px; }
.question-submeta { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: #909399; margin-bottom: 14px; }
.question-content { margin-bottom: 16px; line-height: 1.9; color: #303133; font-size: 16px; }
.question-images { margin: 10px 0; display: flex; flex-wrap: wrap; gap: 10px; }
.question-img { max-width: 480px; max-height: 360px; object-fit: contain; border-radius: 6px; border: 1px solid #e4e7ed; cursor: pointer; transition: transform 0.2s; }
.question-img:hover { transform: scale(1.02); box-shadow: 0 2px 12px rgba(0,0,0,0.12); }
.pq-img-wrap { margin: 10px 0 10px 28px; }
.pq-img { max-width: 420px; max-height: 300px; object-fit: contain; }
.options { margin-bottom: 8px; }
.option-item { display: block; margin: 12px 0; }
.option-rich { display: inline-block; white-space: normal; line-height: 1.7; }
.answer-input { margin-bottom: 12px; }
.correct-row { margin-top: 6px; color: #333; }
.correct { color: #52c41a; font-weight: 600; }
.question-nav-actions { display: flex; align-items: center; gap: 12px; margin-top: 24px; padding-top: 16px; border-top: 1px solid #ebeef5; }
.question-progress { color: #606266; font-size: 14px; }
.paper-actions { display: flex; gap: 12px; justify-content: center; margin-top: 22px; flex-wrap: wrap; }
.paper-empty-print { text-align: center; color: #999; padding: 60px 0; font-size: 15px; }
.paper-header-print { text-align: center; margin-bottom: 12px; }
.paper-title-print { font-size: 22px; font-weight: 700; }
.paper-meta-print { font-size: 13px; color: #666; margin-top: 6px; }
.paper-divider-print { border-top: 2px solid #111; margin: 16px 0 24px; }
.paper-question-print { margin-bottom: 24px; page-break-inside: avoid; }
.pq-stem-print { display: flex; gap: 6px; line-height: 1.8; font-size: 14px; }
.pq-no-print { font-weight: 700; min-width: 24px; }
.pq-options-print { margin-top: 8px; margin-left: 28px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 24px; }
.pq-option-print { font-size: 13px; line-height: 1.7; }

/* 结果弹窗 */
.result-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.rg-item { background: #f8f9fa; padding: 10px; border-radius: 8px; display: flex; justify-content: space-between; }
.rg-item .k { color: #666; }
.rg-item .v { font-weight: 700; }
.rg-item.score { grid-column: span 3; text-align: center; justify-content: center; }
.result-toolbar { margin-top: 12px; }
.wrong-list { margin-top: 12px; display: grid; gap: 8px; }
.wrong-list .item { background: #fff; border: 1px solid #f0f0f0; border-radius: 8px; padding: 8px 10px; }
.wrong-list .title { font-weight: 600; margin-bottom: 4px; }
.wrong-list .row { display: flex; gap: 10px; }
.wrong-list .k { color: #666; }
.wrong-list .knowledge-text { color: #606266; line-height: 1.6; }
.wrong-list .v.success { color: #52c41a; }
.wrong-list .v.danger { color: #ff4d4f; }
.user-answer { color: #e6a23c; font-weight: 600; }
.analysis-row { margin-top: 10px; padding: 10px 12px; background: #fafafa; border-radius: 8px; }
.analysis-label { font-weight: 600; color: #606266; margin-bottom: 6px; }
.analysis-content { color: #303133; line-height: 1.8; }
.collapsible { max-height: 60px; overflow: hidden; transition: max-height 0.3s; }
.collapsible.expanded { max-height: none; }
.toggle-link { font-size: 12px; }
.content :deep(.katex),
.option-rich :deep(.katex),
.wrong-list :deep(.katex) { font-size: 1em; }
.content :deep(.katex-display),
.option-rich :deep(.katex-display),
.wrong-list :deep(.katex-display) { margin: 0.35em 0; text-align: left; overflow-x: auto; overflow-y: hidden; }

@media (max-width: 768px) {
  .nav-header { align-items: flex-start; flex-direction: column; }
  .nav-title { min-width: auto; font-size: 18px; }
  .nav-overview-stats { width: 100%; justify-content: flex-start; overflow-x: auto; padding-bottom: 4px; }
  .nav-overview-stat-item { min-width: 86px; padding: 6px 10px; }
  .nav-overview-stat-number { font-size: 16px; }
  .nav-overview-stat-label { font-size: 11px; }
  .nav-stats { align-self: flex-end; }
  .nav-grid { grid-template-columns: repeat(auto-fill, minmax(45px, 1fr)); gap: 8px; }
  .nav-item { width: 45px; height: 45px; }
  .question-item { padding: 16px; }
  .single-question-header { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
